import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MonitoringAlarm} from '../../models/monitoring-alarm';
import {TranslateService} from '@ngx-translate/core';
import {Message} from 'primeng/api/message';
import {environment} from '../../../../../environments/environment';
import {ProjectConfiguration} from '../../../project/models/project-configuration';
import {RaiseIncidentFormComponent} from '../../../incidents/components/raise-incident-form/raise-incident-form.component';
import {MonitoredTrain} from '../../../monitoring/models/monitored-train';
import {Incident} from '../../../incidents/models/incident';
import {IncidentDataService} from '../../../incidents/services/incident-data.service';
import {MessageService} from 'primeng/api';
import {Subscription} from 'rxjs';

export interface AlarmStatusCategory {
    id: number;
    acknowledged: boolean;
    severity: number | undefined;
    header: string;
    total: number;
    class: string;
    tooltip: string;
}

@Component({
  selector: 'app-monitored-train-alarms',
  template: require('./monitored-train-alarms.component.html'),
  styles: [require('./monitored-train-alarms.component.sass')]
})
export class MonitoredTrainAlarmsComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild(RaiseIncidentFormComponent) raiseIncidentForm: RaiseIncidentFormComponent;
  @Input() projectConfiguration: ProjectConfiguration;
  @Input() alarms: MonitoringAlarm[];
  @Input() monitoredTrains: MonitoredTrain[];
  @Input() projectId: number;
  @Input() isSingleTrain: boolean;
  @Input() selectedAlarmList: MonitoringAlarm[] = [];
  @Output() investigateTrain = new EventEmitter<{
    projectId: number,
    trainId: number
  }>();

    @Output () acknowledgeAlarm = new EventEmitter<MonitoringAlarm[]> ();
    @Output () investigateAlarm = new EventEmitter<MonitoringAlarm[]> ();
    @Output () raiseIncident = new EventEmitter<Incident> ();

    selectedAlarm: MonitoringAlarm | undefined;

  alarmDateFormat = environment.alarms.dateFormat;
  paintedCoachEnabled: boolean;
  isRFEnabled: boolean;
  incidentURL: string;
  currentTrain: MonitoredTrain | undefined;


  filteredAlarms: MonitoringAlarm[];
  tableFilters: any = {};

  languageChangeSubscription: Subscription;

  columns: any[] ;

    tableConfiguration: any = {
        paginate: true,
        sortableMode: 'multiple',
        defaultPagination: 250,
        itemsPerPageOptions: [100, 250, 500],
        exportFilename: 'train-alarms'
    };

    selectAlarmCategoryId = 1;

    alarmStatusCategories: AlarmStatusCategory[] = [{
        id: 1,
        acknowledged: false,
        severity: undefined,
        header: 'I18N_MONITORING.TILE_UNACKNOWLEDGED',
        total: 0,
        class: 'alarm-category-tile danger-tile text-center',
        tooltip: 'I18N_MONITORING.TOOLTIP_UNACKNOWLEDGED'
    }, {
        id: 2,
        acknowledged: false,
        severity: 4,
        header: 'I18N_MONITORING.TILE_HIGH_SEVERITY',
        total: 0,
        class: 'alarm-category-tile high-severity-tile text-center',
        tooltip: 'I18N_MONITORING.TOOLTIP_HIGH_SEVERITY'
    }, {
        id: 3,
        acknowledged: false,
        severity: 3,
        header: 'I18N_MONITORING.TILE_MEDIUM_SEVERITY',
        total: 0,
        class: 'alarm-category-tile medium-severity-tile text-center',
        tooltip: 'I18N_MONITORING.TOOLTIP_MEDIUM_SEVERITY'
    }, {
        id: 4,
        acknowledged: false,
        severity: 2,
        header: 'I18N_MONITORING.TILE_LOW_SEVERITY',
        total: 0,
        class: 'alarm-category-tile low-severity-tile text-center',
        tooltip: 'I18N_MONITORING.TOOLTIP_LOW_SEVERITY'
    }, {
        id: 5,
        acknowledged: true,
        severity: undefined,
        header: 'I18N_MONITORING.TILE_ACKNOWLEDGED',
        total: 0,
        class: 'alarm-category-tile acknowledged-tile  text-center',
        tooltip: 'I18N_MONITORING.TOOLTIP_ACKNOWLEDGED'
    }
    ];

    messages: Message[] = [{
        severity: 'info',
        summary: 'Info',
        detail: this.translate.instant ( 'I18N_MONITORING.NO_ALARMS' )
    }];


    constructor(
        private translate: TranslateService,
        private incidentDataService: IncidentDataService,
        private messageService: MessageService
    ) {
        this.languageChangeSubscription = this.translate.onLangChange.subscribe ( () => {
            this.refreshAlarmTable ();
        } );
    }

    ngOnInit(): void {
    }

    ngOnChanges(): void {
        if (this.isSingleTrain) {
            this.selectAlarmCategoryId = 0;
        }
        this.paintedCoachEnabled = this.projectConfiguration && this.projectConfiguration.paintedCoach;
        const remedyForceConfiguration =  this.projectConfiguration && this.projectConfiguration.remedyForceConfiguration;
        this.isRFEnabled = remedyForceConfiguration && remedyForceConfiguration.rfEnabled;
        this.incidentURL =  remedyForceConfiguration && remedyForceConfiguration.incidentUrl;
        this.refreshAlarmTable ();
    }

    refreshAlarmTable = () => {
        this.columns = [
            {
                header: this.translate.instant ( 'I18N_MONITORING.PROJECT' ),
                field: 'projectName',
                type: 'text'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.TRAIN' ),
                field: 'trainName',
                type: 'text'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.SEVERITY' ),
                field: 'severity'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.DEVICE' ),
                field: 'deviceName',
                type: 'text'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.DESCRIPTION' ),
                field: 'description',
                type: 'text',
                width: '300px'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.PAINTED_CAR_NUMBER' ),
                field: 'paintedCoachId',
                type: 'text'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.DATE' ),
                field: 'lastChange',
                sortOrder: '-1'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.ACKNOWLEDGED' ),
                field: 'acknowledged'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.INCIDENT' ),
                field: 'incidentId',
                type: 'text'
            }, {
                header: this.translate.instant ( 'I18N_MONITORING.STATUS' ),
                field: 'incidentStatus',
                type: 'text'
            }];
        this.columns = this.columns.map ( column => {
            column.visible = true;
            if (column.field === 'projectName') {
                column.visible = !(this.projectId > 0);
            }
            if (column.field === 'paintedCoachId') {
                column.visible = this.paintedCoachEnabled;
            }
            if (column.field === 'incidentId' || column.field === 'incidentStatus') {
                column.visible = this.isRFEnabled;
            }
            return column;
        } );
    }

    clearSelectedAlarms = () => {
        this.selectedAlarmList = [];
    }

    onRaiseRFIncident = () => {
        this.currentTrain = this.monitoredTrains && this.monitoredTrains.find ( train => train.trainId === this.selectedAlarmList[0].trainId );
        this.raiseIncidentForm.showModal ( this.currentTrain, this.selectedAlarmList );
    }

    onAcknowledgeAllAlarms = () => {
        this.selectAlarmCategoryId = 0;
        this.acknowledgeAlarm.emit ( this.alarms.filter ( alarm => !alarm.acknowledged ) );
    }

    onAcknowledgeAlarms = () => {
        this.selectAlarmCategoryId = 0;
        this.acknowledgeAlarm.emit ( this.selectedAlarmList.filter ( alarm => !alarm.acknowledged ) );
    }

    onInvestigateZabbix = () => {
        this.investigateAlarm.emit ( this.selectedAlarmList.filter ( alarm => alarm.type === 'zabbix-alarm' ) );
    }

    onChangeAlarmCategory = (alarmCategory: AlarmStatusCategory) => {
        if (this.selectAlarmCategoryId !== alarmCategory.id) {
            this.clearSelectedAlarms ();
            this.selectAlarmCategoryId = alarmCategory.id;
        }
    }

  onInvestigateTrain = (alarm: MonitoringAlarm) => {
    this.investigateTrain.emit({
      trainId: alarm.trainId,
      projectId: alarm.projectId
    });
  }
  onIncidentIdClick = (incidentPk: string) => {
    if (this.incidentURL) {
      window.open ( this.incidentURL.replace ( 'ID', incidentPk ) );
    } else {
      alert ( 'RemedyForce URL is not found in Project Configuration' );
    }
  }


    handleFilter(data: {
        filters: any,
        filteredValue: MonitoringAlarm[]
    }) {
        this.tableFilters = data.filters;
        if (Object.keys ( this.tableFilters ).length > 0) {
            this.filteredAlarms = data.filteredValue;
        } else {
            this.filteredAlarms = [];
        }

    }

    onSubmitIncident = async (data: { parentType: string, incident: Incident }) => {
        if (data.incident) {
            try {
                const result = await this.incidentDataService.create(data.parentType, data.incident);
                if (!result.errors) {
                    this.onAcknowledgeAlarms();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('I18N_RAISEINCIDENT.RAISE_INCIDENT'),
                        detail: this.translate.instant('I18N_RAISEINCIDENT.SUCCESSFULLY_SUBIMT_INCIDENT')
                    });
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('I18N_RAISEINCIDENT.RAISE_INCIDENT'),
                        detail: result.errors
                    });
                }
            } catch (error) {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('I18N_RAISEINCIDENT.RAISE_INCIDENT'),
                    detail: this.translate.instant('I18N_RAISEINCIDENT.FAILED_TO_SUBMIT_INCIDENT')
                });
            } finally {
                this.raiseIncident.emit();
            }
        }
    }

    ngOnDestroy() {
        this.languageChangeSubscription.unsubscribe();
    }
}
